import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useDebounceFn} from 'ahooks';
import cls from './style.module.scss';

interface AnchorItem {
  key: string;
  title: string;
  content: React.ReactNode;
}

interface AnchorTabContainerProps {
  items: AnchorItem[];
  stickyOffset?: number;
  className?: string;
  headerClassName?: string;
  tabListClassName?: string;
  tabItemClassName?: string;
  tabItemActiveClassName?: string;
  indicatorClassName?: string;
  sectionClassName?: (key: string) => string;
  scrollContainer?: React.RefObject<HTMLElement>;
  containerRef?: React.RefObject<HTMLDivElement>;
  activeKey?: string;
  onTabChange?: (key: string) => void;
}

function AnchorTabContainer({
  items,
  stickyOffset = 0,
  className = '',
  headerClassName = '',
  tabListClassName = '',
  tabItemClassName = '',
  tabItemActiveClassName = '',
  indicatorClassName = '',
  sectionClassName,
  scrollContainer,
  containerRef: externalContainerRef,
  activeKey: externalActiveKey,
  onTabChange,
}: AnchorTabContainerProps) {
  const [internalActiveKey, setInternalActiveKey] = useState<string>(
    items[0]?.key || '',
  );
  const [indicatorStyle, setIndicatorStyle] = useState({left: 0, width: 20});
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const internalContainerRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<{[key: string]: HTMLDivElement}>({});
  const tabItemRefs = useRef<{[key: string]: HTMLDivElement | null}>({});

  const containerRef = externalContainerRef || internalContainerRef;
  const activeKey =
    externalActiveKey !== undefined ? externalActiveKey : internalActiveKey;

  useEffect(() => {
    if (externalActiveKey !== undefined) {
      return;
    }

    const nextDefaultKey = items[0]?.key || '';
    if (!nextDefaultKey) {
      if (internalActiveKey !== '') {
        setInternalActiveKey('');
      }
      return;
    }

    const hasCurrentActive = items.some(item => item.key === internalActiveKey);
    if (!hasCurrentActive || !internalActiveKey) {
      setInternalActiveKey(nextDefaultKey);
    }
  }, [externalActiveKey, internalActiveKey, items]);

  useLayoutEffect(() => {
    const listEl = tabListRef.current;
    const activeEl = activeKey ? tabItemRefs.current[activeKey] : null;

    if (!listEl || !activeEl) {
      setIndicatorStyle({left: 0, width: 20});
      return;
    }

    const left = activeEl.offsetLeft + activeEl.offsetWidth / 2 - 10;
    setIndicatorStyle({left, width: 20});

    const targetScrollLeft =
      activeEl.offsetLeft + activeEl.offsetWidth / 2 - listEl.clientWidth / 2;
    listEl.scrollTo({
      left: Math.max(targetScrollLeft, 0),
      behavior: 'smooth',
    });
  }, [activeKey, items]);

  const {run: handleScroll} = useDebounceFn(
    () => {
      if (!scrollContainer?.current || !containerRef.current) {
        return;
      }

      let currentActiveKey = activeKey;
      const tabsHeight = tabsRef.current?.offsetHeight || 0;
      const tabTop = tabsRef.current?.getBoundingClientRect().top || 0;
      const scrollElement = scrollContainer.current;
      const scrollTop = scrollElement.scrollTop;
      const scrollHeight = scrollElement.scrollHeight;
      const clientHeight = scrollElement.clientHeight;

      const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10;

      if (isScrolledToBottom && items.length > 0) {
        currentActiveKey = items[items.length - 1].key;
      } else {
        for (const item of items) {
          const section = sectionsRef.current[item.key];
          if (!section) {
            continue;
          }

          const rect = section.getBoundingClientRect();
          if (rect.top - tabTop <= tabsHeight + 8) {
            currentActiveKey = item.key;
          } else {
            break;
          }
        }
      }

      if (currentActiveKey !== activeKey) {
        if (externalActiveKey === undefined) {
          setInternalActiveKey(currentActiveKey);
        }
        if (onTabChange && externalActiveKey !== undefined) {
          onTabChange(currentActiveKey);
        }
      }
    },
    {wait: 100},
  );

  useEffect(() => {
    if (!scrollContainer?.current) {
      return;
    }

    const scrollElement = scrollContainer.current;
    handleScroll();
    scrollElement.addEventListener('scroll', handleScroll);

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [
    activeKey,
    items,
    stickyOffset,
    scrollContainer,
    externalActiveKey,
    containerRef,
    handleScroll,
  ]);

  const handleTabChange = (key: string) => {
    if (onTabChange) {
      onTabChange(key);
    } else {
      setInternalActiveKey(key);

      if (scrollContainer?.current) {
        const targetSection = sectionsRef.current[key];
        if (targetSection) {
          const sectionTop = targetSection.offsetTop;
          const tabsHeight = tabsRef.current?.offsetHeight || 0;
          const top = sectionTop - tabsHeight - stickyOffset + 1;
          scrollContainer.current.scrollTo(top, 0);
        }
      }
    }
  };

  return (
    <div ref={containerRef} className={`w-full ${cls.container} ${className}`}>
      <div
        ref={tabsRef}
        className={`${cls.header} ${headerClassName}`}
        style={{top: stickyOffset}}>
        <div ref={tabListRef} className={`${cls.tabList} ${tabListClassName}`}>
          {items.map(item => (
            <div
              key={item.key}
              ref={el => {
                tabItemRefs.current[item.key] = el;
              }}
              onClick={() => handleTabChange(item.key)}
              className={`${cls.tabItem} ${tabItemClassName} ${
                item.key === activeKey
                  ? `${cls.tabItemActive} ${tabItemActiveClassName}`
                  : ''
              }`}>
              {item.title}
            </div>
          ))}
          {items.length > 0 ? (
            <div
              className={`${cls.indicator} ${indicatorClassName}`}
              style={{
                width: `${indicatorStyle.width}px`,
                transform: `translateX(${indicatorStyle.left}px)`,
              }}
            />
          ) : null}
        </div>
      </div>

      <div className="w-full">
        {items.map(item => (
          <div
            key={item.key}
            ref={el => {
              if (el) {
                sectionsRef.current[item.key] = el;
              }
            }}
            className={`w-full ${sectionClassName?.(item.key) || ''}`}
            id={`section-${item.key}`}>
            {item.content}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AnchorTabContainer;
